// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import { queryClient } from './config/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import { SiteTimeProvider } from './contexts/SiteTimeContext';
import SiteRolloutRoster from './contexts/SiteRolloutRoster';
import LaunchDarklyContext from './contexts/LaunchDarklyContext';
import AppShell from './components/shell/AppShell';
import AdminCenterApp from '../admin-center';
import SiteRouteSync from './components/layout/SiteRouteSync';
import SessionGuard from './components/session/SessionGuard';

/**
 * Top-level split between the two apps sharing this bundle (ADR-0014):
 * `/admin-center/*` mounts Admin Center's own shell, everything else mounts
 * Helios' `AppShell`. Neither nests inside the other's chrome. This is the
 * same decision a Module Federation host makes between its own shell and a
 * remote — drawing it here now is what makes that migration (Phase 2) a
 * config change rather than a restructure.
 *
 * `AdminCenterApp` mounts unconditionally here — the LaunchDarkly flag
 * decision lives inside it (AB#29037), below its own `RequireAuth`, not at
 * this route level. An earlier version decided the flag here, before auth
 * resolved: an unauthenticated deep link would evaluate the anonymous
 * context (false), redirect to "/", and lose the URL `RequireAuth`/
 * `ProtectedRoute` would otherwise have preserved through login. Deciding
 * inside, after auth, means the flag only ever evaluates the real signed-in
 * identity (independent review, AB#29037).
 */
function RootShell() {
  return (
    <Routes>
      <Route path="/admin-center/*" element={<AdminCenterApp />} />
      <Route path="/*" element={<AppShell />} />
    </Routes>
  );
}

/**
 * Provider order matters: `QueryClientProvider` sits between `MsalProvider`
 * (index.tsx, fully initialised before render) and `BrowserRouter`, so every
 * route — and `AuthProvider`, which fetches inside the router — can query.
 * `LaunchDarklyContext` sits below `AuthProvider` (not above, as a
 * bare "does LD need auth?" guess might place it) because it reads
 * `useAuth()` to build a real per-user LD context (opaque id, role,
 * persona — see its own comment for why no PII); it falls back to an
 * anonymous context until `/me` resolves, so nothing below it blocks on
 * auth finishing first. `SiteRolloutRoster` sits below that again: it needs
 * the flags, and everything under it — `SiteRouteSync` included — sees only
 * the sites the rollout enables.
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <SessionGuard />
          <LaunchDarklyContext>
            <SiteRolloutRoster>
              <SiteRouteSync>
                <SiteTimeProvider>
                  <RootShell />
                </SiteTimeProvider>
              </SiteRouteSync>
            </SiteRolloutRoster>
          </LaunchDarklyContext>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
